import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import VisualPlacementEditor from './VisualPlacementEditor';
import Toast from './Toast';
import axios from 'axios';
import { API_BASE_URL, TEMPLATE_BASE_URL } from '../config/api';

const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const authAxios = {
  get: (url, config = {}) => axios.get(url, { ...config, headers: { ...config.headers, ...getAuthHeaders() } }),
  put: (url, data, config = {}) => axios.put(url, data, { ...config, headers: { ...config.headers, ...getAuthHeaders() } }),
  post: (url, data, config = {}) => axios.post(url, data, { ...config, headers: { ...config.headers, ...getAuthHeaders() } }),
  delete: (url, config = {}) => axios.delete(url, { ...config, headers: { ...config.headers, ...getAuthHeaders() } })
};

function FormatEditor({ format, onSave, onCancel }) {
  const [searchParams] = useSearchParams();
  const isNew = !format;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('basic'); // 'basic', 'template', 'mockups'
  const [currentOrientation, setCurrentOrientation] = useState('horizontal'); // 'horizontal' or 'vertical'
  const [toast, setToast] = useState(null);
  
  // Check if we should open mockups tab (when returning from mockup editor)
  useEffect(() => {
    if (searchParams.get('tab') === 'mockups') {
      setActiveTab('mockups');
    }
  }, [searchParams]);

  // Helper function to ensure main_template horizontal and vertical are independent
  const ensureIndependentMainTemplate = (mainTemplate) => {
    if (!mainTemplate) {
      return {
        vertical: {
          template_file_path: '',
          template_layer: 'behind',
          background: '#ffffff',
          x_offset: 0,
          y_offset: 0,
          design_width: 470,
          design_height: 470
        },
        horizontal: {
          template_file_path: '',
          template_layer: 'behind',
          background: '#ffffff',
          x_offset: 0,
          y_offset: 0,
          design_width: 470,
          design_height: 470
        },
        output_type: 'jpeg',
        output_quality: 100
      };
    }
    
    // Ensure horizontal and vertical are separate objects
    const normalized = {
      ...mainTemplate,
      horizontal: mainTemplate.horizontal ? { ...mainTemplate.horizontal } : {
        template_file_path: '',
        template_layer: 'behind',
        background: '#ffffff',
        x_offset: 0,
        y_offset: 0,
        design_width: 470,
        design_height: 470
      },
      vertical: mainTemplate.vertical ? { ...mainTemplate.vertical } : {
        template_file_path: '',
        template_layer: 'behind',
        background: '#ffffff',
        x_offset: 0,
        y_offset: 0,
        design_width: 470,
        design_height: 470
      }
    };
    
    return normalized;
  };

  // Helper function to ensure mockups have independent horizontal and vertical
  const ensureIndependentMockups = (mockups) => {
    if (!mockups || !Array.isArray(mockups)) return [];
    
    return mockups.map(mockup => {
      const normalized = { ...mockup };
      // Ensure horizontal and vertical are separate objects
      normalized.horizontal = mockup.horizontal ? { ...mockup.horizontal } : {
        template_file_path: '',
        template_layer: 'behind',
        background: '#ffffff'
      };
      normalized.vertical = mockup.vertical ? { ...mockup.vertical } : {
        template_file_path: '',
        template_layer: 'behind',
        background: '#ffffff'
      };
      return normalized;
    });
  };

  // Update formData when format prop changes (e.g., when returning from mockup editor)
  useEffect(() => {
    if (format) {
      setFormData({
        id: format.id || '',
        name: format.name || '',
        description: format.description || '',
        price: format.price || 0,
        type: format.type || 'Physical',
        main_template: ensureIndependentMainTemplate(format.main_template),
        mockups: ensureIndependentMockups(format.mockups)
      });
    }
  }, [format]);

  const [formData, setFormData] = useState({
    id: format?.id || '',
    name: format?.name || '',
    description: format?.description || '',
    price: format?.price || 0,
    type: format?.type || 'Physical',
    main_template: ensureIndependentMainTemplate(format?.main_template),
    mockups: ensureIndependentMockups(format?.mockups)
  });

  const handleSave = async () => {
    try {
      setLoading(true);
      setError(null);

      // Validate required fields
      if (!formData.id || !formData.name || !formData.price) {
        setError('Please fill in all required fields (ID, Name, Price)');
        setLoading(false);
        return;
      }

      const formatData = { ...formData };

      if (isNew) {
        const response = await authAxios.post(`${API_BASE_URL}/admin/formats`, formatData);
        if (response.data.success) {
          onSave();
        } else {
          setError(response.data.error || 'Failed to create format');
        }
      } else {
        const response = await authAxios.put(`${API_BASE_URL}/admin/formats/${format.id}`, formatData);
        if (response.data.success) {
          onSave();
        } else {
          setError(response.data.error || 'Failed to update format');
        }
      }
    } catch (err) {
      console.error('Error saving format:', err);
      setError(err.response?.data?.error || err.message || 'Failed to save format');
    } finally {
      setLoading(false);
    }
  };

  const updateField = (path, value) => {
    setFormData(prev => {
      const newData = { ...prev };
      const keys = path.split('.');
      let current = newData;
      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) {
          current[keys[i]] = {};
        }
        current = current[keys[i]] = { ...current[keys[i]] };
      }
      current[keys[keys.length - 1]] = value;
      
      // Special handling: If updating main_template.horizontal or main_template.vertical,
      // ensure both orientations remain independent
      if (keys[0] === 'main_template' && (keys[1] === 'horizontal' || keys[1] === 'vertical')) {
        if (newData.main_template) {
          // Ensure both horizontal and vertical are separate objects
          if (newData.main_template.horizontal) {
            newData.main_template.horizontal = { ...newData.main_template.horizontal };
          }
          if (newData.main_template.vertical) {
            newData.main_template.vertical = { ...newData.main_template.vertical };
          }
        }
      }
      
      return newData;
    });
  };

  const getTemplateImageUrl = (orientation) => {
    const templatePath = formData.main_template?.[orientation]?.template_file_path;
    if (!templatePath) return null;
    
    // Convert relative path to API endpoint URL
    // Template paths are like: ./data/assets/templates/template.png
    // Convert to: /api/templates/template.png (or full URL in production)
    if (templatePath.startsWith('./data/assets/templates/')) {
      const filename = templatePath.replace('./data/assets/templates/', '');
      return `${TEMPLATE_BASE_URL}/${filename}`;
    }
    // If it's already a full URL, return as-is
    if (templatePath.startsWith('http://') || templatePath.startsWith('https://')) {
      return templatePath;
    }
    // If it's just a filename, assume it's in templates directory
    if (!templatePath.includes('/')) {
      return `${TEMPLATE_BASE_URL}/${templatePath}`;
    }
    return templatePath;
  };

  return (
    <div className="min-h-screen py-8 px-4 bg-gray-50">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              {isNew ? 'Create New Format' : `Edit Format: ${format.name}`}
            </h1>
            <p className="mt-1 text-sm text-gray-600">Configure format details, templates, and mockups</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={onCancel}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Saving...' : 'Save Format'}
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        {/* Tabs */}
        <div className="mb-6 border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            {['basic', 'template', 'mockups', ...(formData.type === 'Digital' ? ['digital'] : [])].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`py-4 px-1 border-b-2 font-medium text-sm capitalize ${
                  activeTab === tab
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab === 'basic' ? 'Basic Info' : tab === 'template' ? 'Main Template' : tab === 'mockups' ? 'Mockups' : 'Digital Packages'}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="bg-white rounded-lg shadow-md p-6">
          {activeTab === 'basic' && (
            <BasicInfoTab formData={formData} updateField={updateField} isNew={isNew} format={format} onFormatRenamed={onSave} />
          )}

          {activeTab === 'template' && (
            <TemplateTab
              formData={formData}
              updateField={updateField}
              currentOrientation={currentOrientation}
              setCurrentOrientation={setCurrentOrientation}
              getTemplateImageUrl={getTemplateImageUrl}
            />
          )}

          {activeTab === 'mockups' && (
            <MockupsTab
              formData={formData}
              updateField={updateField}
              currentOrientation={currentOrientation}
              setCurrentOrientation={setCurrentOrientation}
              onShowToast={setToast}
            />
          )}

          {activeTab === 'digital' && formData.type === 'Digital' && (
            <DigitalPackagesTab formatId={formData.id} />
          )}
        </div>
      </div>
      
      {/* Toast Notification */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
}

// Basic Info Tab
function BasicInfoTab({ formData, updateField, isNew, format, onFormatRenamed }) {
  const [showRenameForm, setShowRenameForm] = useState(false);
  const [newFormatId, setNewFormatId] = useState('');
  const [renameLoading, setRenameLoading] = useState(false);
  const [renameError, setRenameError] = useState(null);
  const [renameConfirmed, setRenameConfirmed] = useState(false);

  const handleRename = async () => {
    if (!newFormatId.trim()) {
      setRenameError('Please enter a new format ID');
      return;
    }

    if (!renameConfirmed) {
      setRenameError('Please confirm that you understand this will update all historical records');
      return;
    }

    try {
      setRenameLoading(true);
      setRenameError(null);

      const response = await authAxios.put(
        `${API_BASE_URL}/admin/formats/${format.id}/rename`,
        { newFormatId: newFormatId.trim() }
      );

      if (response.data.success) {
        const { updatedRecords } = response.data.data;
        alert(
          `Format renamed successfully!\n\n` +
          `Updated records:\n` +
          `- Formats: ${updatedRecords.formats}\n` +
          `- Digital Packages: ${updatedRecords.digitalPackages}\n` +
          `- Orders: ${updatedRecords.orders}\n` +
          `- Download Tokens: ${updatedRecords.downloadTokens}`
        );
        setShowRenameForm(false);
        setNewFormatId('');
        setRenameConfirmed(false);
        // Refresh the format data
        if (onFormatRenamed) {
          onFormatRenamed();
        }
      } else {
        setRenameError(response.data.error || 'Failed to rename format');
      }
    } catch (err) {
      console.error('Error renaming format:', err);
      setRenameError(err.response?.data?.error || err.message || 'Failed to rename format');
    } finally {
      setRenameLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Format ID <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.id}
            onChange={(e) => updateField('id', e.target.value)}
            disabled={!isNew}
            className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              !isNew ? 'bg-gray-100 cursor-not-allowed' : ''
            }`}
            placeholder="e.g., NoteCard01"
          />
          {!isNew && (
            <div className="mt-2">
              <p className="text-xs text-gray-500 mb-2">Format ID cannot be changed directly</p>
              <button
                onClick={() => setShowRenameForm(!showRenameForm)}
                className="text-sm text-blue-600 hover:text-blue-800 underline"
              >
                {showRenameForm ? 'Cancel Rename' : 'Rename Format ID'}
              </button>
            </div>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Format Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => updateField('name', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="e.g., A2 NoteCards (4 cards per pack)"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Price <span className="text-red-500">*</span>
          </label>
          <input
            type="number"
            step="0.01"
            value={formData.price}
            onChange={(e) => updateField('price', parseFloat(e.target.value) || 0)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="0.00"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Type <span className="text-red-500">*</span>
          </label>
          <select
            value={formData.type}
            onChange={(e) => updateField('type', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="Physical">Physical</option>
            <option value="Digital">Digital</option>
          </select>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Description
        </label>
        <textarea
          value={formData.description}
          onChange={(e) => updateField('description', e.target.value)}
          rows={4}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="Describe the format..."
        />
      </div>

      {/* Rename Format Section */}
      {!isNew && showRenameForm && (
        <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Rename Format ID</h3>
          <p className="text-sm text-gray-700 mb-4">
            <strong className="text-yellow-800">Warning:</strong> Renaming a format ID will update all references across the system, including:
          </p>
          <ul className="text-sm text-gray-700 mb-4 list-disc list-inside space-y-1">
            <li>All digital package entries</li>
            <li>All historical orders</li>
            <li>All active download tokens</li>
            <li>The format definition itself</li>
          </ul>

          {renameError && (
            <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm text-red-600">{renameError}</p>
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Current Format ID
              </label>
              <input
                type="text"
                value={formData.id}
                disabled
                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 cursor-not-allowed"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                New Format ID <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={newFormatId}
                onChange={(e) => {
                  setNewFormatId(e.target.value);
                  setRenameError(null);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., NoteCards02Digital"
              />
              <p className="mt-1 text-xs text-gray-500">
                Only alphanumeric characters and underscores allowed
              </p>
            </div>

            <div className="flex items-start">
              <input
                type="checkbox"
                id="rename-confirm"
                checked={renameConfirmed}
                onChange={(e) => {
                  setRenameConfirmed(e.target.checked);
                  setRenameError(null);
                }}
                className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="rename-confirm" className="ml-2 text-sm text-gray-700">
                I understand that this will update all historical records and cannot be easily undone
              </label>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={handleRename}
                disabled={renameLoading || !newFormatId.trim() || !renameConfirmed}
                className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {renameLoading ? 'Renaming...' : 'Rename Format ID'}
              </button>
              <button
                onClick={() => {
                  setShowRenameForm(false);
                  setNewFormatId('');
                  setRenameConfirmed(false);
                  setRenameError(null);
                }}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Template Tab
function TemplateTab({ formData, updateField, currentOrientation, setCurrentOrientation, getTemplateImageUrl }) {
  const template = formData.main_template || {};
  const orientationData = template[currentOrientation] || {};
  const [imageDimensions, setImageDimensions] = useState({ naturalWidth: 0, naturalHeight: 0, displayWidth: 0, displayHeight: 0 });

  const handleImageLoad = (e) => {
    const img = e.target;
    // Use a small delay to ensure the image is fully rendered and dimensions are accurate
    setTimeout(() => {
      setImageDimensions({
        naturalWidth: img.naturalWidth,
        naturalHeight: img.naturalHeight,
        displayWidth: img.offsetWidth || img.clientWidth,
        displayHeight: img.offsetHeight || img.clientHeight
      });
    }, 10);
  };

  const handleImageResize = () => {
    const img = document.querySelector(`#template-preview-${currentOrientation}`);
    if (img && img.complete) {
      setImageDimensions({
        naturalWidth: img.naturalWidth,
        naturalHeight: img.naturalHeight,
        displayWidth: img.offsetWidth,
        displayHeight: img.offsetHeight
      });
    }
  };

  useEffect(() => {
    window.addEventListener('resize', handleImageResize);
    // Recalculate when orientation changes
    setTimeout(handleImageResize, 100);
    return () => window.removeEventListener('resize', handleImageResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- handleImageResize is stable, currentOrientation triggers recalc
  }, [currentOrientation]);

  // Reset dimensions when template file path changes
  useEffect(() => {
    setImageDimensions({ naturalWidth: 0, naturalHeight: 0, displayWidth: 0, displayHeight: 0 });
  }, [orientationData.template_file_path, currentOrientation]);

  // Recalculate display dimensions when offsets or design dimensions change (to ensure overlay updates)
  useEffect(() => {
    if (imageDimensions.naturalWidth > 0 && imageDimensions.naturalHeight > 0) {
      const img = document.querySelector(`#template-preview-${currentOrientation}`);
      if (img && img.complete) {
        setImageDimensions(prev => ({
          ...prev,
          displayWidth: img.offsetWidth || img.clientWidth,
          displayHeight: img.offsetHeight || img.clientHeight
        }));
      }
    }
  }, [orientationData.x_offset, orientationData.y_offset, orientationData.design_width, orientationData.design_height, currentOrientation, imageDimensions.naturalWidth, imageDimensions.naturalHeight]);

  // Calculate overlay rectangle position and size
  const getOverlayStyle = () => {
    if (!imageDimensions.naturalWidth || !imageDimensions.naturalHeight || !imageDimensions.displayWidth || !imageDimensions.displayHeight) {
      return { display: 'none' };
    }

    const xOffset = orientationData.x_offset || 0;
    const yOffset = orientationData.y_offset || 0;
    const designWidth = orientationData.design_width || 470;
    const designHeight = orientationData.design_height || 470;

    // Calculate scale factor between natural and displayed image
    // Ensure we have valid dimensions to avoid division by zero
    const scaleX = imageDimensions.naturalWidth > 0 ? imageDimensions.displayWidth / imageDimensions.naturalWidth : 0;
    const scaleY = imageDimensions.naturalHeight > 0 ? imageDimensions.displayHeight / imageDimensions.naturalHeight : 0;

    // Calculate overlay position and size in displayed coordinates
    // These should match exactly what Sharp does: composite at (xOffset, yOffset) with size (designWidth, designHeight)
    const overlayLeft = xOffset * scaleX;
    const overlayTop = yOffset * scaleY;
    const overlayWidth = designWidth * scaleX;
    const overlayHeight = designHeight * scaleY;

    // Debug logging
    console.log('[Overlay Debug]', {
      naturalSize: `${imageDimensions.naturalWidth}x${imageDimensions.naturalHeight}`,
      displaySize: `${imageDimensions.displayWidth}x${imageDimensions.displayHeight}`,
      scale: `${scaleX.toFixed(4)}x${scaleY.toFixed(4)}`,
      offsets: `x:${xOffset}, y:${yOffset}`,
      designSize: `${designWidth}x${designHeight}`,
      overlayPos: `left:${overlayLeft.toFixed(2)}, top:${overlayTop.toFixed(2)}`,
      overlaySize: `${overlayWidth.toFixed(2)}x${overlayHeight.toFixed(2)}`
    });

    return {
      position: 'absolute',
      left: `${overlayLeft}px`,
      top: `${overlayTop}px`,
      width: `${overlayWidth}px`,
      height: `${overlayHeight}px`,
      border: '2px solid #ef4444',
      backgroundColor: 'rgba(239, 68, 68, 0.1)',
      pointerEvents: 'none',
      boxSizing: 'border-box',
      zIndex: 50
    };
  };

  return (
    <div className="space-y-6">
      {/* Orientation Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {['horizontal', 'vertical'].map((orientation) => (
            <button
              key={orientation}
              onClick={() => setCurrentOrientation(orientation)}
              className={`py-2 px-1 border-b-2 font-medium text-sm capitalize ${
                currentOrientation === orientation
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {orientation}
            </button>
          ))}
        </nav>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Template Configuration Form */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900">Template Configuration ({currentOrientation})</h3>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Template File Path
            </label>
            <input
              type="text"
              value={orientationData.template_file_path || ''}
              onChange={(e) => updateField(`main_template.${currentOrientation}.template_file_path`, e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
              placeholder="./data/assets/templates/template.png"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Template Layer
            </label>
            <select
              value={orientationData.template_layer || 'behind'}
              onChange={(e) => updateField(`main_template.${currentOrientation}.template_layer`, e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="behind">Behind Design</option>
              <option value="front">In Front of Design</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Background Color
            </label>
            <input
              type="text"
              value={orientationData.background || '#ffffff'}
              onChange={(e) => updateField(`main_template.${currentOrientation}.background`, e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="#ffffff"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">X Offset</label>
              <input
                type="number"
                value={orientationData.x_offset || 0}
                onChange={(e) => updateField(`main_template.${currentOrientation}.x_offset`, parseInt(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Y Offset</label>
              <input
                type="number"
                value={orientationData.y_offset || 0}
                onChange={(e) => updateField(`main_template.${currentOrientation}.y_offset`, parseInt(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Design Width</label>
              <input
                type="number"
                value={orientationData.design_width || 470}
                onChange={(e) => updateField(`main_template.${currentOrientation}.design_width`, parseInt(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Design Height</label>
              <input
                type="number"
                value={orientationData.design_height || 470}
                onChange={(e) => updateField(`main_template.${currentOrientation}.design_height`, parseInt(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Output Type</label>
              <select
                value={template.output_type || 'jpeg'}
                onChange={(e) => updateField('main_template.output_type', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="jpeg">JPEG</option>
                <option value="png">PNG</option>
                <option value="pdf">PDF</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Output Quality</label>
              <input
                type="number"
                min="1"
                max="100"
                value={template.output_quality || 100}
                onChange={(e) => updateField('main_template.output_quality', parseInt(e.target.value) || 100)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>

        {/* Template Preview */}
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Template Preview ({currentOrientation})</h3>
          {getTemplateImageUrl(currentOrientation) ? (
            <div className="border-2 border-gray-300 rounded-lg p-4 bg-gray-50">
              <div className="relative inline-block max-w-full" style={{ position: 'relative' }}>
                <img
                  id={`template-preview-${currentOrientation}`}
                  src={getTemplateImageUrl(currentOrientation)}
                  alt="Template preview"
                  className="max-w-full h-auto rounded block"
                  onLoad={handleImageLoad}
                  onError={(e) => {
                    e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="400"%3E%3Crect fill="%23f3f4f6" width="400" height="400"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dy=".3em" fill="%239ca3af" font-family="Arial" font-size="16"%3EImage not found%3C/text%3E%3C/svg%3E';
                  }}
                />
                {/* Red box overlay - hidden but code preserved for troubleshooting */}
                {/* To re-enable: change display: 'none' to display: 'block' in getOverlayStyle() */}
                <div style={getOverlayStyle()} />
              </div>
            </div>
          ) : (
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center text-gray-500">
              Enter a template file path to see preview
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Mockups Tab
function MockupsTab({ formData, updateField, currentOrientation, setCurrentOrientation, onShowToast }) {
  const navigate = useNavigate();

  const handleAddMockup = () => {
    const newMockup = {
      name: `Mockup ${(formData.mockups?.length || 0) + 1}`,
      vertical: {
        template_file_path: '',
        template_layer: 'behind',
        background: '#ffffff'
      },
      horizontal: {
        template_file_path: '',
        template_layer: 'behind',
        background: '#ffffff'
      },
      output_type: 'jpeg',
      output_quality: 100,
      placements: []
    };
    
    const mockups = [...(formData.mockups || []), newMockup];
    updateField('mockups', mockups);
    // Navigate to edit the newly created mockup, passing the mockup data via state
    navigate(`/admin/formats/${formData.id}/mockups/${mockups.length - 1}`, {
      state: { 
        mockup: newMockup,
        format: formData 
      }
    });
  };

  const handleDeleteMockup = (index) => {
    if (window.confirm('Delete this mockup?')) {
      const mockups = formData.mockups.filter((_, i) => i !== index);
      updateField('mockups', mockups);
    }
  };

  const handleMoveMockup = (index, direction) => {
    const mockups = [...formData.mockups];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (newIndex < 0 || newIndex >= mockups.length) {
      return; // Can't move outside array bounds
    }
    
    // Swap the mockups
    [mockups[index], mockups[newIndex]] = [mockups[newIndex], mockups[index]];
    updateField('mockups', mockups);
    
    // Show reminder to save
    if (onShowToast) {
      onShowToast({ 
        message: 'Mockup order changed. Don\'t forget to save!', 
        type: 'warning' 
      });
    }
  };

  // Helper function to get thumbnail URL for a mockup
  const getMockupThumbnailUrl = (mockup) => {
    // Try current orientation first, then fall back to vertical, then horizontal
    const orientation = currentOrientation || 'vertical';
    const templatePath = mockup[orientation]?.template_file_path || 
                        mockup.vertical?.template_file_path || 
                        mockup.horizontal?.template_file_path;
    
    if (!templatePath) return null;
    
    // Convert relative path to API endpoint URL
    if (templatePath.startsWith('./data/assets/templates/')) {
      const filename = templatePath.replace('./data/assets/templates/', '');
      return `${TEMPLATE_BASE_URL}/${filename}`;
    }
    // If it's already a full URL, return as-is
    if (templatePath.startsWith('http://') || templatePath.startsWith('https://')) {
      return templatePath;
    }
    // If it's just a filename, assume it's in templates directory
    if (!templatePath.includes('/')) {
      return `${TEMPLATE_BASE_URL}/${templatePath}`;
    }
    return templatePath;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">
          Mockups ({formData.mockups?.length || 0})
        </h3>
        <button
          onClick={handleAddMockup}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          + Add Mockup
        </button>
      </div>

      {/* Mockups Grid */}
      {formData.mockups && formData.mockups.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {formData.mockups.map((mockup, index) => {
            const thumbnailUrl = getMockupThumbnailUrl(mockup);
            return (
              <div 
                key={index} 
                className="border border-gray-300 rounded-lg overflow-hidden bg-white relative"
              >
                {/* Reorder Arrows - Above Image */}
                <div className="absolute top-2 left-1/2 transform -translate-x-1/2 z-10 flex gap-2">
                  <button
                    onClick={() => handleMoveMockup(index, 'up')}
                    disabled={index === 0}
                    className={`px-2 py-1 text-sm rounded transition-colors shadow-md ${
                      index === 0
                        ? 'text-gray-300 cursor-not-allowed bg-white opacity-50'
                        : 'text-gray-700 bg-white hover:bg-gray-100 hover:text-gray-900'
                    }`}
                    title="Move left"
                  >
                    ←
                  </button>
                  <button
                    onClick={() => handleMoveMockup(index, 'down')}
                    disabled={index === formData.mockups.length - 1}
                    className={`px-2 py-1 text-sm rounded transition-colors shadow-md ${
                      index === formData.mockups.length - 1
                        ? 'text-gray-300 cursor-not-allowed bg-white opacity-50'
                        : 'text-gray-700 bg-white hover:bg-gray-100 hover:text-gray-900'
                    }`}
                    title="Move right"
                  >
                    →
                  </button>
                </div>

                {/* Thumbnail */}
                <div className="aspect-square bg-gray-100 overflow-hidden relative">
                  {thumbnailUrl ? (
                    <img
                      src={thumbnailUrl}
                      alt={mockup.name || `Mockup ${index + 1}`}
                      className="w-full h-full object-contain"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <div 
                    className="w-full h-full flex items-center justify-center text-gray-400 text-sm"
                    style={{ display: thumbnailUrl ? 'none' : 'flex' }}
                  >
                    No thumbnail
                  </div>
                </div>

                {/* Card Content */}
                <div className="p-4">
                  <h4 className="font-medium text-gray-900 mb-3 truncate" title={mockup.name || `Mockup ${index + 1}`}>
                    {mockup.name || `Mockup ${index + 1}`}
                  </h4>
                  
                  <div className="flex gap-2">
                    <button
                      onClick={() => navigate(`/admin/formats/${formData.id}/mockups/${index}`)}
                      className="flex-1 px-3 py-2 text-sm text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded transition-colors"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteMockup(index)}
                      className="flex-1 px-3 py-2 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-8 text-gray-500">
          No mockups yet. Click "Add Mockup" to create one.
        </div>
      )}
    </div>
  );
}

// Mockup Editor Component (kept for potential inline editing; currently mockup edit uses MockupEditorPage)
// eslint-disable-next-line no-unused-vars
function MockupEditor({ mockup, index, formData, updateField, currentOrientation, setCurrentOrientation }) {
  const [placements, setPlacements] = useState(mockup.placements || []);

  const handlePlacementsChange = (newPlacements) => {
    setPlacements(newPlacements);
    const mockups = [...formData.mockups];
    mockups[index] = { ...mockups[index], placements: newPlacements };
    updateField('mockups', mockups);
  };

  const getTemplateImageUrl = (orientation) => {
    const templatePath = mockup[orientation]?.template_file_path;
    if (!templatePath) return null;
    
    // Convert relative path to API endpoint URL
    // Template paths are like: ./data/assets/templates/template.png
    // Convert to: /api/templates/template.png (or full URL in production)
    if (templatePath.startsWith('./data/assets/templates/')) {
      const filename = templatePath.replace('./data/assets/templates/', '');
      return `${TEMPLATE_BASE_URL}/${filename}`;
    }
    // If it's already a full URL, return as-is
    if (templatePath.startsWith('http://') || templatePath.startsWith('https://')) {
      return templatePath;
    }
    // If it's just a filename, assume it's in templates directory
    if (!templatePath.includes('/')) {
      return `${TEMPLATE_BASE_URL}/${templatePath}`;
    }
    return templatePath;
  };

  return (
    <div className="space-y-6 mt-4">
      {/* Mockup Name */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Mockup Name</label>
        <input
          type="text"
          value={mockup.name || ''}
          onChange={(e) => {
            const mockups = [...formData.mockups];
            mockups[index] = { ...mockups[index], name: e.target.value };
            updateField('mockups', mockups);
          }}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      {/* Orientation Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {['horizontal', 'vertical'].map((orientation) => (
            <button
              key={orientation}
              onClick={() => setCurrentOrientation(orientation)}
              className={`py-2 px-1 border-b-2 font-medium text-sm capitalize ${
                currentOrientation === orientation
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {orientation}
            </button>
          ))}
        </nav>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Mockup Configuration */}
        <div className="space-y-4">
          <h4 className="font-medium text-gray-900">Template Configuration ({currentOrientation})</h4>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Template File Path</label>
            <input
              type="text"
              value={mockup[currentOrientation]?.template_file_path || ''}
              onChange={(e) => {
                const mockups = [...formData.mockups];
                // Ensure we create new objects to maintain independence
                const updatedMockup = { ...mockups[index] };
                // Ensure both horizontal and vertical are separate objects
                updatedMockup.horizontal = mockups[index].horizontal ? { ...mockups[index].horizontal } : {};
                updatedMockup.vertical = mockups[index].vertical ? { ...mockups[index].vertical } : {};
                // Update only the current orientation
                updatedMockup[currentOrientation] = {
                  ...updatedMockup[currentOrientation],
                  template_file_path: e.target.value
                };
                mockups[index] = updatedMockup;
                updateField('mockups', mockups);
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Template Layer</label>
            <select
              value={mockup[currentOrientation]?.template_layer || 'behind'}
              onChange={(e) => {
                const mockups = [...formData.mockups];
                // Ensure we create new objects to maintain independence
                const updatedMockup = { ...mockups[index] };
                // Ensure both horizontal and vertical are separate objects
                updatedMockup.horizontal = mockups[index].horizontal ? { ...mockups[index].horizontal } : {};
                updatedMockup.vertical = mockups[index].vertical ? { ...mockups[index].vertical } : {};
                // Update only the current orientation
                updatedMockup[currentOrientation] = {
                  ...updatedMockup[currentOrientation],
                  template_layer: e.target.value
                };
                mockups[index] = updatedMockup;
                updateField('mockups', mockups);
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="behind">Behind Design</option>
              <option value="front">In Front of Design</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Background Color</label>
            <input
              type="text"
              value={mockup[currentOrientation]?.background || '#ffffff'}
              onChange={(e) => {
                const mockups = [...formData.mockups];
                // Ensure we create new objects to maintain independence
                const updatedMockup = { ...mockups[index] };
                // Ensure both horizontal and vertical are separate objects
                updatedMockup.horizontal = mockups[index].horizontal ? { ...mockups[index].horizontal } : {};
                updatedMockup.vertical = mockups[index].vertical ? { ...mockups[index].vertical } : {};
                // Update only the current orientation
                updatedMockup[currentOrientation] = {
                  ...updatedMockup[currentOrientation],
                  background: e.target.value
                };
                mockups[index] = updatedMockup;
                updateField('mockups', mockups);
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Output Type</label>
              <select
                value={mockup.output_type || 'jpeg'}
                onChange={(e) => {
                  const mockups = [...formData.mockups];
                  mockups[index].output_type = e.target.value;
                  updateField('mockups', mockups);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="jpeg">JPEG</option>
                <option value="png">PNG</option>
                <option value="pdf">PDF</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Output Quality</label>
              <input
                type="number"
                min="1"
                max="100"
                value={mockup.output_quality || 100}
                onChange={(e) => {
                  const mockups = [...formData.mockups];
                  mockups[index].output_quality = parseInt(e.target.value) || 100;
                  updateField('mockups', mockups);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>

        {/* Template Preview */}
        <div>
          <h4 className="font-medium text-gray-900 mb-4">Template Preview ({currentOrientation})</h4>
          {getTemplateImageUrl(currentOrientation) ? (
            <div className="border-2 border-gray-300 rounded-lg p-4 bg-gray-50">
              <img
                src={getTemplateImageUrl(currentOrientation)}
                alt="Template preview"
                className="max-w-full h-auto rounded"
                onError={(e) => {
                  e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="400"%3E%3Crect fill="%23f3f4f6" width="400" height="400"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dy=".3em" fill="%239ca3af" font-family="Arial" font-size="16"%3EImage not found%3C/text%3E%3C/svg%3E';
                }}
              />
            </div>
          ) : (
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center text-gray-500">
              Enter a template file path to see preview
            </div>
          )}
        </div>
      </div>

      {/* Visual Placement Editor */}
      <div className="mt-6">
        <h4 className="font-medium text-gray-900 mb-4">Placements</h4>
        <VisualPlacementEditor
          templateImageUrl={getTemplateImageUrl(currentOrientation)}
          placements={placements}
          onPlacementsChange={handlePlacementsChange}
          orientation={currentOrientation}
          designWidth={mockup[currentOrientation]?.design_width || 240}
          designHeight={mockup[currentOrientation]?.design_height || 240}
        />
      </div>
    </div>
  );
}

// Digital Packages Tab
function DigitalPackagesTab({ formatId }) {
  const [packages, setPackages] = useState([]);
  const [designs, setDesigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingPackage, setEditingPackage] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({
    designId: '',
    packagePath: ''
  });

  useEffect(() => {
    if (formatId) {
      fetchPackages();
      fetchDesigns();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchPackages/fetchDesigns run on formatId change only
  }, [formatId]);

  const fetchPackages = async () => {
    try {
      setLoading(true);
      const response = await authAxios.get(`${API_BASE_URL}/admin/digital-packages/${formatId}`);
      if (response.data.success) {
        setPackages(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching digital packages:', err);
      setError(err.response?.data?.error || 'Failed to load digital packages');
    } finally {
      setLoading(false);
    }
  };

  const fetchDesigns = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/designs`);
      if (response.data.success) {
        setDesigns(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching designs:', err);
    }
  };

  const handleAdd = () => {
    setEditingPackage(null);
    setFormData({ designId: '', packagePath: '' });
    setShowAddForm(true);
    setError(null);
  };

  const handleEdit = (pkg) => {
    setEditingPackage(pkg);
    setFormData({
      designId: pkg.designId,
      packagePath: pkg.packagePath
    });
    setShowAddForm(true);
    setError(null);
  };

  const handleDelete = async (designId) => {
    if (!window.confirm(`Are you sure you want to delete the digital package for design ${designId}?`)) {
      return;
    }

    try {
      const response = await authAxios.delete(`${API_BASE_URL}/admin/digital-packages/${designId}/${formatId}`);
      if (response.data.success) {
        await fetchPackages();
      } else {
        setError(response.data.error || 'Failed to delete package');
      }
    } catch (err) {
      console.error('Error deleting package:', err);
      setError(err.response?.data?.error || err.message || 'Failed to delete package');
    }
  };

  const handleSave = async () => {
    if (!formData.designId || !formData.packagePath) {
      setError('Please fill in all fields');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      if (editingPackage) {
        // Update existing
        const response = await authAxios.put(
          `${API_BASE_URL}/admin/digital-packages/${formData.designId}/${formatId}`,
          { packagePath: formData.packagePath }
        );
        if (response.data.success) {
          await fetchPackages();
          setShowAddForm(false);
          setEditingPackage(null);
        } else {
          setError(response.data.error || 'Failed to update package');
        }
      } else {
        // Create new
        const response = await authAxios.post(`${API_BASE_URL}/admin/digital-packages`, {
          designId: formData.designId,
          formatId: formatId,
          packagePath: formData.packagePath
        });
        if (response.data.success) {
          await fetchPackages();
          setShowAddForm(false);
          setFormData({ designId: '', packagePath: '' });
        } else {
          setError(response.data.error || 'Failed to create package');
        }
      }
    } catch (err) {
      console.error('Error saving package:', err);
      setError(err.response?.data?.error || err.message || 'Failed to save package');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setShowAddForm(false);
    setEditingPackage(null);
    setFormData({ designId: '', packagePath: '' });
    setError(null);
  };

  const getDesignName = (designId) => {
    const design = designs.find(d => d.id === designId);
    return design ? design.title : designId;
  };

  if (!formatId) {
    return (
      <div className="text-center py-8 text-gray-500">
        <p>Please save the format first to manage digital packages.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Digital Packages</h3>
          <p className="text-sm text-gray-500 mt-1">
            Manage digital download packages for this format. Each entry maps a design to a package file path.
          </p>
        </div>
        <button
          onClick={handleAdd}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          Add Package
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {showAddForm && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
          <h4 className="text-md font-semibold text-gray-900 mb-4">
            {editingPackage ? 'Edit Digital Package' : 'Add Digital Package'}
          </h4>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Design <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.designId}
                onChange={(e) => setFormData({ ...formData, designId: e.target.value })}
                disabled={!!editingPackage}
                className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  editingPackage ? 'bg-gray-100 cursor-not-allowed' : ''
                }`}
              >
                <option value="">Select a design...</option>
                {designs.map(design => (
                  <option key={design.id} value={design.id}>
                    {design.id} - {design.title}
                  </option>
                ))}
              </select>
              {editingPackage && (
                <p className="mt-1 text-xs text-gray-500">Design cannot be changed after creation</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Package Path <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.packagePath}
                onChange={(e) => setFormData({ ...formData, packagePath: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., ./data/assets/digital_packages/DESIGNID-FORMATID.zip"
              />
              <p className="mt-1 text-xs text-gray-500">
                Relative path from server root to the zip file containing the digital package
              </p>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={handleSave}
                disabled={loading}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {loading ? 'Saving...' : 'Save'}
              </button>
              <button
                onClick={handleCancel}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {loading && packages.length === 0 ? (
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-2 text-gray-600">Loading packages...</p>
        </div>
      ) : packages.length === 0 ? (
        <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-200">
          <p className="text-gray-500">No digital packages configured for this format.</p>
          <p className="text-sm text-gray-400 mt-2">Click "Add Package" to create one.</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Design
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Package Path
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {packages.map((pkg) => (
                <tr key={`${pkg.designId}-${pkg.formatId}`}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{pkg.designId}</div>
                    <div className="text-sm text-gray-500">{getDesignName(pkg.designId)}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900 font-mono">{pkg.packagePath}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => handleEdit(pkg)}
                      className="text-blue-600 hover:text-blue-900 mr-4"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(pkg.designId)}
                      className="text-red-600 hover:text-red-900"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default FormatEditor;

